import { useState, useEffect, useCallback, useRef } from 'react';
import { jobService } from '../services/job';

interface ActiveJob {
  id: string;
  status: string;
  updated_at: string;
}

interface UseJobPollingOptions {
  enabled?: boolean;
  interval?: number;
  onJobUpdate?: (jobId: string, oldStatus: string, newStatus: string) => void;
  onStatsUpdate?: () => void;
}

export function useJobPolling(options: UseJobPollingOptions = {}) {
  const {
    enabled = true,
    interval = 5000,
    onJobUpdate,
    onStatsUpdate
  } = options;

  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [lastPoll, setLastPoll] = useState<Date | null>(null);
  const activeJobsRef = useRef<Map<string, string>>(new Map());

  const checkActiveJobs = useCallback(async () => {
    try {
      const response = await jobService.getActiveJobsStatus();
      
      if (response.success) {
        const newActiveJobs = response.data.activeJobs;
        
        // Check for status changes
        let hasChanges = false;
        newActiveJobs.forEach(job => {
          const previousStatus = activeJobsRef.current.get(job.id);
          if (previousStatus && previousStatus !== job.status) {
            // Job status changed
            hasChanges = true;
            if (onJobUpdate) {
              onJobUpdate(job.id, previousStatus, job.status);
            }
          }
        });

        // Check for completed jobs (no longer in active list)
        activeJobsRef.current.forEach((status, jobId) => {
          const stillActive = newActiveJobs.find(j => j.id === jobId);
          if (!stillActive) {
            // Job is no longer active (completed/failed/etc)
            hasChanges = true;
            // Don't update status here as we don't know the final status
            // The job will be fetched with its final status on the next full refresh
          }
        });

        // Update our reference map
        activeJobsRef.current.clear();
        newActiveJobs.forEach(job => {
          activeJobsRef.current.set(job.id, job.status);
        });

        // If there were changes, trigger stats update
        if (hasChanges && onStatsUpdate) {
          onStatsUpdate();
        }

        setActiveJobs(newActiveJobs);
        setLastPoll(new Date());
        
        return response.data.hasActiveJobs;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to poll active jobs:', error);
      return false;
    }
  }, [onJobUpdate, onStatsUpdate]);

  useEffect(() => {
    if (!enabled) {
      setIsPolling(false);
      return;
    }

    // Initial check
    checkActiveJobs().then(hasActive => {
      setIsPolling(hasActive);
    });

    // Set up polling interval
    const pollInterval = setInterval(async () => {
      const hasActive = await checkActiveJobs();
      setIsPolling(hasActive);
      
      // Stop polling if no active jobs
      if (!hasActive) {
        clearInterval(pollInterval);
      }
    }, interval);

    return () => clearInterval(pollInterval);
  }, [enabled, interval, checkActiveJobs]);

  return {
    activeJobs,
    isPolling,
    lastPoll,
    activeCount: activeJobs.length,
    refresh: checkActiveJobs
  };
}